import { Component, HostListener, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { QuestionnaireService } from '../../services/questionnaire/questionnaire.service';
import { AlertBannerService, AlertType } from '../../lib/alert-banner/alert-banner.service';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { PreviewQuestionnaireComponent } from '../../components/preview-questionnaire/preview-questionnaire.component';
import { CreateSurveyFromScratchComponent } from '../../components/create-survey-from-scratch/create-survey-from-scratch.component';
import moment from 'moment-timezone';
import * as Cookies from 'js-cookie';
import { ConstantsService } from 'src/app/services/constants/constants.service';
import { CreateReferenceSurveyComponent } from '../create-reference-survey/create-reference-survey.component';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';

@Component({
  selector: 'app-create-survey',
  templateUrl: './create-survey.component.pug',
  styleUrls: ['./create-survey.component.scss'],
  standalone: false
})
export class CreateSurveyComponent implements OnInit {
  surveyType = '';
  org_name='';
  questionnaires: any[] = [];
  total = 5;
  rowsPerView = 20;
  currentPage = 1;
  previewQuestionnaireComponent: BsModalRef = new BsModalRef();
  createSurveyFromScratchComponent: BsModalRef = new BsModalRef();
  createReferenceSurveyFromScratchComponent: BsModalRef = new BsModalRef();
  loading=false;
  sortString='-name';
  searchOption: any = undefined;
  searchOptions = [{'label':$localize`Survey name`,'value':'title', 'hint':$localize`Specific survey`},
                   {'label':$localize`Created by`,'value':'name', 'hint':$localize`Survey creator`},
                   {'label':$localize`Last updated by`,'value':'name', 'hint':$localize`Last survey editor`},];
  selectedColumns: any = [];
  addOrder = 0;
  sortCriteria = {
    sortOptions: [
      {
        'label': $localize`Created by name`,
        'type': "word",
        'value': "name"
      },
      {
        'label': $localize`Survey name`,
        'type': "word",
        'value': "title"
      },
      {
        'label': $localize`Times used`,
        'type': "number",
        'value': "count"
      },
    ],
    sortString: '-name',
    sortOrder: ''
  }
  startingPoint="template";
  selectedSurvey=0;
  org_uid = Cookies.get(ConstantsService.ORG_COOKIE);
  attachToFooter = false;
  max_questions_limit = 5;
  survey_type_map = QuestionnaireService.survey_type_mapping;
  canCreateSurvey = false;


  constructor(
    private router: Router,
    private questionnaireService: QuestionnaireService,
    private organisationService: OrganisationService,
    private alertList: AlertBannerService,
    private modalService: BsModalService,
    public ngZone: NgZone,
    private permission: PermissionsService
  ) {
    this.surveyType = this.router?.getCurrentNavigation()?.extras?.state?.surveyType;
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll(e) {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
      //At bottom of page
      this.ngZone.run(() => { this.attachToFooter = true })
    } else if ((window.innerHeight + window.scrollY) < (document.body.offsetHeight-100)||window.scrollY==0) {
      // No longer at bottom of page
      this.ngZone.run(() => { this.attachToFooter = false })
    }
  }

  ngOnInit(): void {
    this.permission.setNavCheck(false);
    if(!this.surveyType){
      const type = localStorage.getItem('survey_type');
      this.surveyType = type ? type : 'exit';
    }
    localStorage.setItem('survey_type', String(this.surveyType));
    this.organisationService.getOrganisation().subscribe(res => {
      this.org_name = res['brand_name'];
      this.max_questions_limit = res['questionnaire_limit'];
    });
    this.canCreateSurvey = this.permission.hasPermission('can_create_reference_surveys') ||
      this.permission.hasPermission('can_create_pulse_surveys') ||
      this.permission.hasPermission('can_create_exit_surveys')
    if(!this.canCreateSurvey){
      this.permission.setNavCheck(true);
      this.router.navigate(['/no-access']);
    }

    if(this.canCreateSurvey){
      this.initialiseQuestionnaires();
    }

  }


  setFormattedDate(questionnaire){
    if(questionnaire['created']){
      questionnaire['created_formatted'] = moment(questionnaire['created']).format('ll');
    }
    if(questionnaire['modified']){
      questionnaire['modified_formatted'] = moment(questionnaire['modified']).format('ll');
    }
  }

  initialiseQuestionnaires(){
    this.loading=false;
    this.questionnaires=[];
    const is_default = this.startingPoint=='template';
    const type = this.survey_type_map[this.surveyType] ? this.survey_type_map[this.surveyType] : 1;
    this.questionnaireService.getQuestionnairesDuplicateParams(
      this.searchOption,
      this.rowsPerView,
      this.currentPage,
      this.sortString,
      is_default,
      type,
      undefined,
      true,
      [{'key': 'active', 'value': true}]
    ).subscribe(
      (res) => {
        this.questionnaires = res.data.questionnaires;
        this.total = res.data.total;
        this.loading=true;
        if(this.total){
          this.selectedSurvey = this.questionnaires[0]['id'];
          this.questionnaires.forEach(q=>this.setFormattedDate(q));
        }
        this.loading=true;
      }, error => {
        this.loading=false;
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      });
  }

  onSortChange(sortString:any): void {
    this.sortString = sortString;
    this.initialiseQuestionnaires();
  }

  onPageChange(page:any): void {
    this.currentPage = page;
    this.initialiseQuestionnaires();
  }

  onPageSizeChange(rows:any): void {
    this.rowsPerView = rows;
    this.initialiseQuestionnaires();
  }

  onSearchChange(searchOutput:any){
    if( searchOutput && searchOutput['data'] ) {
      this.searchOption = searchOutput['data']['search'];
    } else {
      this.searchOption = [];
    }
    this.initialiseQuestionnaires();
  }

  openPreview(id) {
    const initialState={
      'questionnaireId': id,
      'createSurvey': true,
      'surveyType': this.surveyType
    };
    this.previewQuestionnaireComponent = this.modalService.show(
      PreviewQuestionnaireComponent,
      { class: 'modal-dialog-centered xl-popup', initialState},
    )
    this.previewQuestionnaireComponent.content.nextClick.subscribe(res=>{
      const isNew = true;
      this.router.navigateByUrl('surveys/create', { state:{'questionnaireId':id, isNew} });
    })
  }

  startFromTemplate(){
    this.startingPoint = 'template';
    this.initialiseQuestionnaires();
  }

  copyFromExisting(){
    this.startingPoint = 'copy';
    this.initialiseQuestionnaires();
  }

  startFromScratch(){
    if (this.surveyType=='exit' || this.surveyType=='pulse'){
      this.startBrandNewSurvey();
    }
    if (this.surveyType=='reference'){
      this.startBrandNewReferenceSurvey();
    }
  }
  startBrandNewSurvey(){
    const initialState = {
      'max_questions_limit': this.max_questions_limit,
      'survey_type': this.surveyType
    }
    this.createSurveyFromScratchComponent = this.modalService.show(
      CreateSurveyFromScratchComponent,
        Object.assign({},{'class': 'modal-dialog-centered modal-dialog-scrollable modal-lg', initialState})
    )
    this.createSurveyFromScratchComponent.content.cancelClick.subscribe(res=>{
      this.startingPoint = 'template';
      this.initialiseQuestionnaires();
    })
    this.createSurveyFromScratchComponent.content.skipClick.subscribe(res=>{
      const isNew = true;
      this.router.navigateByUrl('surveys/create', { state:{ isNew }});
    })
    this.createSurveyFromScratchComponent.content.nextClick.subscribe(res=>{
      const isNew = true;
      this.router.navigateByUrl('surveys/create', { state:{'selectedMetrics':res['selected_metrics'].join(','), 'questionnaireTitle': res['title'], isNew} });
    })
  }

  private showCreateReferenceModal(initialState: any) {
    this.createReferenceSurveyFromScratchComponent = this.modalService.show(
      CreateReferenceSurveyComponent,
      Object.assign({}, { 'class': 'modal-dialog-centered xl-popup', initialState })
    );

    this.createReferenceSurveyFromScratchComponent.content.cancelClick.subscribe(res => {
      this.startingPoint = 'template';
      this.initialiseQuestionnaires();
    });

    this.createReferenceSurveyFromScratchComponent.content.skipClick.subscribe(res => {
      const isNew = true;
      this.router.navigateByUrl('surveys/create', {
        state: {
          'selectedRefType': res['selectedRefType'],
          'questionnaireTitle': res['title'],
          'isOptInQuestionEnabled': res['isOptInQuestionEnabled'],
          'optInQuestion': res['optInQuestion'],
          isNew,
          'minReferee': res['minReferee'],
          'noteToCandidate': res['noteToCandidate'],
          'noteToReferee': res['noteToReferee'],
          'noteToCandidateChange': res['noteToCandidateChange'],
          'noteToRefereeChange': res['noteToRefereeChange'],
        }
      });
    });

    this.createReferenceSurveyFromScratchComponent.content.nextClick.subscribe(res => {
      const isNew = true;
      this.router.navigateByUrl('surveys/create', {
        state: {
          'selectedRefType': res['selectedRefType'],
          'questionnaireTitle': res['title'],
          'isOptInQuestionEnabled': res['isOptInQuestionEnabled'],
          'optInQuestion': res['optInQuestion'],
          isNew,
          'minReferee': res['minReferee'],
          'noteToCandidate': res['noteToCandidate'],
          'noteToReferee': res['noteToReferee'],
          'noteToCandidateChange': res['noteToCandidateChange'],
          'noteToRefereeChange': res['noteToRefereeChange'],
        }
      });
    });

  }


  startBrandNewReferenceSurvey(){

    this.organisationService.getOrganisation().subscribe((orgDetails) => {
      const initialState: any = {
          'max_questions_limit': this.max_questions_limit,
          'noteToCandidate': orgDetails.notes.note_to_candidate,
          'noteToReferee': orgDetails.notes.note_to_referee,
          'defaultNoteToCandidate': orgDetails.notes.note_to_candidate,
          'defaultNoteToReferee': orgDetails.notes.note_to_referee,
      };
      this.showCreateReferenceModal(initialState);
    }, error => {
      console.error('Failed to fetch organisation details:', error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Failed to load organisation settings. Please try again.`,
        keepOnRouteChange: true
      }]);
    })
  }
  goBack(){
    this.router.navigateByUrl('surveys')
    this.permission.setNavCheck(true);
  }
  createSurvey(){
    this.permission.setNavCheck(true);
    const isNew = true;
    if(this.questionnaires.length > 0) {
      const questionnaireId = this.questionnaires.find(q=>q.id==this.selectedSurvey)['questionnaire']['id'];
      this.router.navigateByUrl('surveys/create', { state: { questionnaireId, isNew } });
    } else {
      this.router.navigateByUrl('surveys/create', { state:{ isNew }});
    }
  }
}
